import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';

/**
 * Creates a formatted string that includes the beginning and end
 * currency symbols along with any needed decimal and thousands
 * separator symbols.
 *
 * @example // Example assuming USD currency context:
 *  formatCurrencyString("5")   // returns "$5.00"
 *  formatCurrencyString("3.7") // returns "$3.70"
 */
export function formatCurrencyString(cost: number) {
	const {symbol, position} = MerchantConfiguration.currency.configuration();

	if (typeof cost !== 'number') {
		cost = 0;
	}

	let formattedCurrency = '';
	if (position === 'left' || position === 'left_space') {
		let negSymbol = '';
		let formattedCost = formatCostString(cost);
		if (cost < 0) {
			negSymbol = '−';
			formattedCost = formatCostString(Math.abs(cost));
		}

		formattedCurrency = `${negSymbol}${symbol}${position === 'left_space' ? ' ' : ''}${formattedCost}`;
	} else {
		formattedCurrency = `${formatCostString(cost)}${position === 'right_space' ? ' ' : ''}${symbol}`;
	}

	return formattedCurrency;
}
/**
 * Creates a formatted string that includes the decimal and
 * thousands separator symbols to fixed decimal length.
 *
 * @example // Example assuming USD currency context:
 *  formatCostString("5")   // returns "5.00"
 *  formatCostString("3.7") // returns "3.70"
 */

// eslint-disable-next-line complexity
export function formatCostString(cost: number): string {
	const {code, thousands_separator: thousandsSeparator, decimal_separator: decimalSeparator, rounding, number_of_decimals: decimals} = MerchantConfiguration.currency.configuration();

	if (typeof cost !== 'number') {
		cost = 0;
	}

	if (code === 'JPY' || code === 'HUF') {
		return cost.toString();
	}

	// We would prefer to always default to 2 decimal points event if the store sets decimal point to 0
	const numberOfDecimals = decimals || 2;

	switch (rounding) {
		case 'up':
			switch (numberOfDecimals) {
				case 0:
					cost = Math.ceil(cost);
					break;
				case 1:
					cost = Math.ceil(cost * 10) / 10;
					break;
				case 2:
					cost = Math.ceil(cost * 100) / 100;
					break;
				case 3:
					cost = Math.ceil(cost * 1000) / 1000;
					break;
				default:
					cost = Math.ceil(cost);
					break;
			}

			break;
		case 'down':
			switch (numberOfDecimals) {
				case 0:
					cost = Math.floor(cost);
					break;
				case 1:
					cost = Math.floor(cost * 10) / 10;
					break;
				case 2:
					cost = Math.floor(cost * 100) / 100;
					break;
				case 3:
					cost = Math.floor(cost * 1000) / 1000;
					break;
				default:
					cost = Math.floor(cost);
					break;
			}

			break;
		case 'nearest':
			// In the future support may need added for rounding to a specific 0.50, 0.10 type situation. That would go here.
			switch (numberOfDecimals) {
				case 0:
					cost = Math.round(cost);
					break;
				case 1:
					cost = Math.round(cost * 10) / 10;
					break;
				case 2:
					cost = Math.round(cost * 100) / 100;
					break;
				case 3:
					cost = Math.round(cost * 1000) / 1000;
					break;
				default:
					cost = Math.round(cost);
					break;
			}

			break;
		default:
			break;
	}

	// This is to make sure if rounding is disabled and decimals are set to zero that the decimals are cut off because when
	// decimal length is zero we actually render 3 decimal points.
	cost = Number.parseFloat(cost.toFixed(decimals));

	let formattedPrice = '';
	try {
		const currencySplit = cost.toFixed(numberOfDecimals).split('.');
		let dollarAmount = currencySplit[0];
		const centsAmount = currencySplit[1] ?? '';

		// We reverse the string because we want to chunk numbers from back of  the string and not front. Eases the operation of chunking.
		const rev = currencySplit?.[0]?.split('').reverse().join('') ?? '';
		const revFormat = rev.match(/.{1,3}/g)?.join(thousandsSeparator) ?? '';
		dollarAmount = revFormat.split('').reverse().join('');
		formattedPrice += dollarAmount;
		if (centsAmount !== '') {
			formattedPrice += decimalSeparator + centsAmount;
		}

		return formattedPrice;
	} catch {
		return cost.toFixed(decimals);
	}
}
